<template>
    <nav>
      <AlertMessages
          v-if="alertValue"
          :alert-type="alertType"
          :alert-msg="alertMsg"
          @reset="alertValue = false"
      />
      <v-row>
        <v-col
          cols="11"
          class="mx-auto"
        >
          <p 
            :class="mobile() ? 'text-h5 font-weight-bold ml-16 mr-16' : 'text-h5 font-weight-bold'"
            :align="mobile() ? 'center' : 'left'"
          >
            {{ $t('emailTemplatesTitle') }}
          </p>
        </v-col>
      </v-row>
      <v-row 
          v-if="templates.length > 0 && !loadingData"
          align="center"
          class="mx-auto"
      >
          <v-col
              cols="11"
              class="mx-auto px-0"
          >
              <v-card v-for="template in templates" :key="template.id" rounded="xxl" class="px-6 mb-3">
                  <v-card-title data-testid="template-title" class="row-pointer px-0" @click="showEditor(template)">{{ $t(`template${template.attributes.name}Title`) }}</v-card-title>
                  <v-card-text v-if="template.show" class="px-0">
                      <v-expand-transition>
                          <div>
                              <v-card-subtitle class="pa-0 greytext--text">{{ $t(`template${template.attributes.name}Text`) }}</v-card-subtitle>
                              <div class="languages custom-tab-menu d-flex my-5">
                                  <v-list class="py-0" flat>
                                      <v-list-item @click="setLanguage(template, 'en')" :class="language == 'en' ? 'accent--text' : ''">
                                          {{ $t("english") }}
                                      </v-list-item>
                                      <v-list-item @click="setLanguage(template, 'sv')" :class="language == 'sv' ? 'accent--text' : ''">
                                          {{ $t("swedish") }}
                                      </v-list-item>
                                  </v-list>
                              </div>
                              <v-form
                                  ref="form"
                                  v-model="valid"
                                  @submit.prevent
                              >
                                  <p class="text-body-2 font-weight-bold mb-2 darktext--text">
                                      {{ $t('labelSubject') }}
                                  </p>
                                  <v-text-field
                                      :value="template[`${language}EmailCustomization`] && template[`${language}EmailCustomization`].attributes && template[`${language}EmailCustomization`].attributes.subject ? template[`${language}EmailCustomization`].attributes.subject : ''"
                                      data-testid="subject-input"
                                      solo
                                      elevation="6"
                                      class="rounded-xl text-body-2"
                                      @input="updateSubject(template, $event)"
                                  />
                                  <p class="text-body-2 font-weight-bold mb-2 mt-4 darktext--text">
                                      {{ $t('labelBody') }}
                                  </p>
                                  <editor-email-template 
                                      class="editor editor-email-template"
                                      data-testid="body-input"
                                      :key="updateEditor"
                                      :content="template[`${language}EmailCustomization`] && template[`${language}EmailCustomization`].attributes && template[`${language}EmailCustomization`].attributes.body ? template[`${language}EmailCustomization`].attributes.body : ''"
                                      :template="template"
                                      @updateContent="updateContent"
                                  />
                                  <span v-if="requiredShortCodes[template.attributes.name]" class="ml-4 error--text text-caption">{{ $t(`template${template.attributes.name}BodyError`) }}</span>
                                  <p class="text-body-2 font-weight-bold mb-2 mt-6 darktext--text">
                                      {{ $t('labelShortCodes') }}
                                  </p>
                                  <v-list dense data-testid="shortcodes-list">
                                      <v-list-item v-for="shortCode in filteredShortCodes" :key="shortCode.id" class="text-body-2">
                                              {{ shortCode.name }} - {{ shortCode.description }}   
                                              <span v-if="shortCodesSubject[template.attributes.name].includes(shortCode.name)">&nbsp;{{ $t('canBeUsedInSubjectBody') }}</span>
                                              <span v-else>&nbsp;{{ $t('canBeUsedInBody') }}</span>
                                      </v-list-item>
                                  </v-list>
                                  <v-card-actions class="px-0 d-block d-md-flex">
                                      <v-btn
                                          ref="submit"
                                          data-testid="submit-button"
                                          type="submit"
                                          color="accent"
                                          min-width="100"
                                          dark
                                          elevation="0"
                                          class="rounded-xxl text-none px-4 submit mt-2 ml-0 mr-2"
                                          @click="saveUpdateCustomization(template)"
                                          >
                                              {{ language == 'en' ? $t('saveEnVersion') : $t('saveSvVersion') }}
                                      </v-btn>
                                      <v-btn 
                                          v-if="template[`${language}EmailCustomization`] && template[`${language}EmailCustomization`].id" 
                                          ref="reset"
                                          data-testid="reset-button"
                                          color="greytext"
                                          min-width="100"
                                          dark
                                          elevation="0"
                                          class="rounded-xxl text-none px-4 reset mt-2 ml-0"
                                          @click="deleteTemplateCustomization(template)">
                                              {{ language == 'en' ? $t('resetEnVersion') : $t('resetSvVersion') }}
                                      </v-btn>
                                  </v-card-actions>
                              </v-form>
                          </div>
                      </v-expand-transition>
                  </v-card-text>
              </v-card>
          </v-col>
      </v-row>
    </nav>
  </template>
  <script>
  import i18n from '../../plugins/i18n'
  import { getEmailTemplates, getEmailCustomizations, createEmailCustomization, updateEmailCustomization, deleteEmailCustomization } from '../../api/spaceApi'
  import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin'
  import EditorEmailTemplate from '../../components/EditorEmailTemplate';
  import AlertMessages from '../../components/AlertMessages';
  import Vue from "vue";
  
  export default {
      components: {
          EditorEmailTemplate,
          AlertMessages
      },
      mixins: [repeatedMethodsMixin],
      data(){
          return{
              spaceId: window.$cookies.get('space_id'),
              alertValue: false,
              alertType: '',
              alertMsg: '',
              templates: [],
              template: null,
              customizations: [],
              customization: {
                  subject: null,
                  body: null,
              },
              language: 'en',
              languages: ['en', 'sv'],
              updateEditor: null,
              loadingData: false,
              valid: true,
              shortCodes:[
                  {
                      name: '[SPACE_URL]', description: i18n.t('templateSpaceUrlDescription'), template: ['activity_share', 'hub_share', 'activities_added_into_hub', 'feed_item_unlocked', 'space_invitation']
                  },
                  {
                      name: '[INVITED_BY]', description: i18n.t('templateInvitedByDescription'), template: ['activity_share', 'hub_share']
                  },
                  {
                      name: '[ACTIVITY_TITLE]', description: i18n.t('templateActivityTitleDescription'), template: ['activity_share', 'feed_item_unlocked']
                  },
                  {
                      name: '[ACTIVITY_URL]', description: i18n.t('templateActivityUrlDescription'), template: ['activity_share', 'feed_item_unlocked']
                  },
                  {
                      name: '[ACTIVITY_URL_BUTTON]', description: i18n.t('templateActivityUrlButtonDescription'), template: ['activity_share', 'feed_item_unlocked']
                  },
                  {
                      name: '[ACTIVITY_IMAGE]', description: i18n.t('templateActivityImageDescription'), template: ['activity_share']
                  },
                  {
                      name: '[HUB_TITLE]', description: i18n.t('templateHubTitleDescription'), template: ['hub_share', 'activities_added_into_hub']
                  },
                  {
                      name: '[HUB_URL]', description: i18n.t('templateHubUrlDescription'), template: ['hub_share', 'activities_added_into_hub']
                  },
                  {
                      name: '[HUB_URL_BUTTON]', description: i18n.t('templateHubUrlButtonDescription'), template: ['hub_share', 'activities_added_into_hub']
                  },
                  {
                      name: '[HUB_IMAGE]', description: i18n.t('templateHubImageDescription'), template: ['hub_share', 'activities_added_into_hub']
                  },
                  {
                      name: '[MESSAGE_CONTENT]', description: i18n.t('templateMessageDescription'), template: ['activity_share', 'hub_share']
                  },
                  {
                      name: '[SPACE_SUBDOMAIN]', description: i18n.t('templateSpaceSubdomainDescription') + (' (' + window.location.host.split('.')[0] + '.' + process.env.VUE_APP_DOMAIN + ')'), template: []
                  },
                  {
                      name: '[JOIN_URL]', description: i18n.t('templateJoinUrlDescription'), template: ['space_invitation']
                  },
                  {
                      name: '[JOIN_URL_BUTTON]', description: i18n.t('templateJoinUrlButtonDescription'), template: ['space_invitation']
                  }
              ],
  
              shortCodesSubject: {
                  'activity_share': ['[ACTIVITY_TITLE]', '[INVITED_BY]'],
                  'hub_share': ['[HUB_TITLE]', '[INVITED_BY]'],
                  'space_invitation': ['[SPACE_URL]'],
                  'activities_added_into_hub': ['[HUB_TITLE]'],
                  'feed_item_unlocked': ['[ACTIVITY_TITLE]']
              },
  
              requiredShortCodes: {
                  'activity_share': ['[ACTIVITY_URL]', '[ACTIVITY_URL_BUTTON]'],
                  'hub_share': ['[HUB_URL]', '[HUB_URL_BUTTON]'],
                  'space_invitation': ['[JOIN_URL]', '[JOIN_URL_BUTTON]']
              }
          }
      },
  
      computed: {
          filteredShortCodes() {
              return this.shortCodes.filter((item) =>{
                  return item.template.includes(this.template)
              });
          },
      },
  
      async created(){
          this.loadingData = true
          await this.fetchEmailTemplates()
          await this.fetchEmailCustomizations()
          this.loadingData = false
      },
      methods: {
  
          setLanguage(template, language){
              this.language = language
              this.updateEditor = Date.now()
          },
  
          async fetchEmailTemplates(){
              try {
                  const response = await getEmailTemplates()
                  if(response.status){
                      if(response.status == 200) {
                          this.templates = response.data.data
                      }
                  }
              } catch (e) {
                  if(e.response){
                      this.alertValue = true
                      if(e.response.status == 403){
                          this.$router.push({name: 'PageAccessDenied'})
                      } else {
                          this.alertType = 'error'
                          this.alertMsg =  i18n.t('serverError')
                      }
                  }
              }
          },
          async fetchEmailCustomizations(){
              try {
                  const response = await getEmailCustomizations(this.spaceId)
                  if(response.status){
                      if(response.status == 200) {
                          this.customizations = response.data.data
                          this.updateData(this.templates, this.customizations)
                      }
                  }
              } catch (e) {
                  if(e.response){
                      this.alertValue = true
                      if(e.response.status == 403){
                          this.$router.push({name: 'PageAccessDenied'})
                      } else {
                          this.alertType = 'error'
                          this.alertMsg =  i18n.t('serverError')
                      }
                  }
              }
          },
  
          updateData(templates, customizations){
              for (const template of templates) {
                  const emailTemplateId = template.id;
                  const enEmailCustomization =customizations.find(
                      customization => customization.relationships.emailTemplate.data.id === emailTemplateId && customization.attributes.language === 'en'
                  );
                  const svEmailCustomization =customizations.find(
                      customization => customization.relationships.emailTemplate.data.id === emailTemplateId && customization.attributes.language === 'sv'
                  );
                  enEmailCustomization ? this.$set(template, 'enEmailCustomization', enEmailCustomization) : this.$set(template, 'enEmailCustomization', null)
                  svEmailCustomization ? this.$set(template, 'svEmailCustomization', svEmailCustomization) : this.$set(template, 'svEmailCustomization', null)
              }
          },
  
          async showEditor(template){
              Vue.set(template, "show", !template.show);
              this.template = template.attributes.name
              for (let i = 0; i < this.templates.length; i++) {
                  if (template.id != this.templates[i].id) {
                      Vue.set(this.templates[i], "show", false);
                  }
              }
          },
  
          saveUpdateCustomization(template){
  
              var validBody = true
              const emailCustomization = `${this.language}EmailCustomization`;
              if (template.attributes.name in this.requiredShortCodes) {
                  if(template[emailCustomization] && template[emailCustomization].attributes && template[emailCustomization].attributes.body){
                      if(!this.checkRequiredShortCodes(template.attributes.name, template[emailCustomization].attributes.body)){
                          validBody = false
                      }
                  }
              }
  
              if(validBody){
                  if(template[emailCustomization] &&  template[emailCustomization].id){
                      this.updateCustomization(template)
                  }else {
                      this.saveCustomization(template)
                  }
              }else {
                  this.alertValue = true
                  this.alertType = 'error'
                  this.alertMsg =  i18n.t('templateRequiredShortCodesError')
              }
          },
  
          checkRequiredShortCodes(name, body){
              return (this.requiredShortCodes[name]).some(code => body.includes(code))
          },
  
          async saveCustomization(template){
              const emailCustomization = `${this.language}EmailCustomization`;
              try {
                  let data = {
                      data: {
                          type: "email-customizations",
                          attributes: {
                              subject: template[emailCustomization].attributes.subject,
                              body: template[emailCustomization].attributes.body,
                              language: this.language
                          },
                          relationships: {
                              space: {
                                  data: {
                                      type: "spaces",
                                      id: this.spaceId
                                  }
                              },
                              emailTemplate: {
                                  data: {
                                      type: "email-templates",
                                      id: template.id
                                  }
                              }
                          }
                      }
                  }
  
                  const response = await createEmailCustomization(data)
                  if(response.status){
                      if(response.status == 201) {
                          template[emailCustomization] = response.data.data
                          this.alertValue = true
                          this.alertType = 'success'
                          this.alertMsg =  i18n.t('emailTemplateUpdateSuccess')
                      }
                  }
              } catch (e) {
                  if(e.response){
                      this.alertValue = true
                      if(e.response.status == 403){
                          this.$router.push({name: 'PageAccessDenied'})
                      } else if (e.response.status == 422 ){
                          for(let i = 0; i < e.response.data.errors.length; i++){
                              if(e.response.data.errors[i].detail == 'The data.attributes.body does not contain required codes.'){
                                  this.alertType = 'error'
                                  this.alertMsg = i18n.t("templateRequiredShortCodesError");
                              }else {
                                  this.alertType = 'error'
                                  this.alertMsg = i18n.t('serverError')
                              }
                          }
                      }
                      else {
                          this.alertType = 'error'
                          this.alertMsg =  i18n.t('serverError')
                      }
                  }
              }
          },
  
          async updateCustomization(template){
              const emailCustomization = `${this.language}EmailCustomization`;
              try {
                  let data = {
                      data: {
                          type: "email-customizations",
                          id: template[emailCustomization].id,
                          attributes: {
                              subject: template[emailCustomization].attributes.subject,
                              body: template[emailCustomization].attributes.body,
                              language: this.language
                          },
                          relationships: {
                              space: {
                                  data: {
                                      type: "spaces",
                                      id: this.spaceId
                                  }
                              },
                              emailTemplate: {
                                  data: {
                                      type: "email-templates",
                                      id: template.id
                                  }
                              }
                          }
                      }
                  }
  
                  const response = await updateEmailCustomization(template[emailCustomization].id, data)
                  if(response.status){
                      if(response.status == 200) {
                          template[emailCustomization] = response.data.data
                          this.alertValue = true
                          this.alertType = 'success'
                          this.alertMsg =  i18n.t('emailTemplateUpdateSuccess')
                      }
                  }
              } catch (e) {
                  if(e.response){
                      this.alertValue = true
                      if(e.response.status == 403){
                          this.$router.push({name: 'PageAccessDenied'})
                      }else if (e.response.status == 422 ){
                          for(let i = 0; i < e.response.data.errors.length; i++){
                              if(e.response.data.errors[i].detail == 'The data.attributes.body does not contain required codes.'){
                                  this.alertType = 'error'
                                  this.alertMsg = i18n.t("templateRequiredShortCodesError");
                              }else {
                                  this.alertType = 'error'
                                  this.alertMsg = i18n.t('serverError')
                              }
                          }
                      }else {
                          this.alertType = 'error'
                          this.alertMsg =  i18n.t('serverError')
                      }
                      return false
                  }
              }
          },
  
          async deleteTemplateCustomization(template){
              const emailCustomization = `${this.language}EmailCustomization`;
              try {
                  const response = await deleteEmailCustomization(template[emailCustomization].id)
                  if(response.status){
                      if(response.status == 204) {
                          template[emailCustomization] = null
                          this.updateEditor = Date.now()
                          this.alertValue = true
                          this.alertType = 'success'
                          this.alertMsg =  i18n.t('emailTemplateResetSuccess')
                      }
                  }
              } catch (e) {
                  if(e.response){
                      this.alertValue = true
                      if(e.response.status == 403){
                          this.$router.push({name: 'PageAccessDenied'})
                      } else {
                          this.alertType = 'error'
                          this.alertMsg =  i18n.t('serverError')
                      }
                  }
              }
          },
          updateSubject(template, subject){
              if(this.strip(subject).trim() == ''){
                  subject = null
              }
              const emailCustomization = `${this.language}EmailCustomization`;
              if(template[emailCustomization] == null){
                  template[emailCustomization] = {
                      attributes: {
                          subject: subject,
                          body: null,
                          language: this.language
  
                      }
                  }
              }else{
                  template[emailCustomization].attributes.subject = subject
              }
          },
          updateContent(content, template){
              if(this.strip(content).trim() == ''){
                  content = null
              }
              const emailCustomization = `${this.language}EmailCustomization`;
              if(template[emailCustomization] == null){
                  template[emailCustomization] = {
                      attributes: {
                          subject: null,
                          body: content,
                          language: this.language
                      }
                  }
              }else{
                  template[emailCustomization].attributes.body = content
              }
          }
      }
      
  }
  </script>